<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import aiDashboardAPI from 'dashboard/api/aiDashboard';
import { downloadXlsxFile } from 'dashboard/helper/xlsxHelper';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

// ── Date range (default 7 days) ─────────────────────────────────────────────
const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 6);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate = ref(today.toISOString().slice(0, 10));

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(false);
const error = ref(null);
const data = ref(null);
// Histórico completo (sempre tudo, independente do filtro) — usado nos 4 KPIs totais
const allData = ref(null);

// ── Fetch ────────────────────────────────────────────────────────────────────
async function fetchDashboard() {
  loading.value = true;
  error.value = null;
  try {
    const res = await aiDashboardAPI.get({
      from: fromDate.value,
      to: toDate.value,
    });
    data.value = res.data;
  } catch (e) {
    error.value =
      e?.response?.data?.error || e.message || 'Erro ao carregar dados';
  } finally {
    loading.value = false;
  }
}

// Histórico completo, do início até hoje — não depende do filtro de datas
async function fetchAllTime() {
  try {
    const res = await aiDashboardAPI.get({
      from: '2015-01-01',
      to: new Date().toISOString().slice(0, 10),
      monthly: 1,
    });
    allData.value = res.data;
  } catch (e) {
    allData.value = null;
  }
}

onMounted(() => {
  fetchDashboard();
  fetchAllTime();
});
watch([fromDate, toDate], fetchDashboard);

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtSecs(secs) {
  if (!secs || secs <= 0) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

function pct(num, den) {
  if (!den) return '0%';
  return `${((num / den) * 100).toFixed(1)}%`;
}

// ── Computed ─────────────────────────────────────────────────────────────────
const d = computed(() => data.value || {});

// KPIs "totais" — agora respeitam o filtro de datas (usam os dados do período)
const totals = computed(() => {
  const a = data.value || {};
  return {
    conversations: a.total_conversations || 0,
    avgSeconds: a.real_conversation_median_seconds || 0,
    csatScore: a.csat?.satisfaction_score ?? 0,
    handoffRate: a.handoff_rate ?? 0,
  };
});

// Evolução mensal (histórico completo) — série por mês vinda do backend
const monthlyEvolution = computed(() => allData.value?.monthly_evolution ?? []);

const MONTH_NAMES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const MONTH_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// Anos disponíveis (mais recente primeiro) + ano selecionado no filtro
const availableYears = computed(() => {
  const years = new Set(monthlyEvolution.value.map(m => m.month.split('/')[1]));
  return Array.from(years).sort((a, b) => Number(b) - Number(a));
});
const selectedYear = ref(null);
watch(
  availableYears,
  years => {
    if (
      years.length &&
      (selectedYear.value === null || !years.includes(selectedYear.value))
    ) {
      selectedYear.value = years[0];
    }
  },
  { immediate: true }
);

// Meses do ano selecionado — o gráfico e os totais respeitam o filtro
const filteredMonthly = computed(() =>
  monthlyEvolution.value.filter(
    m => m.month.split('/')[1] === selectedYear.value
  )
);

// ── Drill-down: mês selecionado → evolução dia a dia ─────────────────────────
// selectedMonth = null → visão mês a mês do ano. '01'..'12' → visão diária do mês.
const selectedMonth = ref(null);
const dailyData = ref(null);
const dailyLoading = ref(false);

// Meses que têm dados no ano selecionado (para o seletor de mês)
const availableMonths = computed(() =>
  filteredMonthly.value.map(m => m.month.split('/')[0])
);

const isDailyView = computed(() => selectedMonth.value !== null);
const dailyEvolution = computed(() => dailyData.value?.daily_evolution ?? []);

// Ao trocar de ano, volta para a visão mensal (mês deixa de fazer sentido)
watch(selectedYear, () => {
  selectedMonth.value = null;
});

// Busca a série diária do mês selecionado (from = dia 1, to = último dia do mês)
async function fetchDaily() {
  if (!selectedYear.value || !selectedMonth.value) {
    dailyData.value = null;
    return;
  }
  const y = selectedYear.value;
  const m = selectedMonth.value;
  const lastDay = new Date(Number(y), Number(m), 0).getDate();
  const from = `${y}-${m}-01`;
  const to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
  dailyLoading.value = true;
  try {
    const res = await aiDashboardAPI.get({ from, to, daily: 1 });
    dailyData.value = res.data;
  } catch (e) {
    dailyData.value = null;
  } finally {
    dailyLoading.value = false;
  }
}
watch([selectedYear, selectedMonth], fetchDaily);

function selectMonth(monthNum) {
  selectedMonth.value = monthNum;
}
function backToMonthly() {
  selectedMonth.value = null;
}

// Rótulo legível do mês selecionado (ex.: "Julho de 2026")
const selectedMonthLabel = computed(() => {
  if (!isDailyView.value) return '';
  return `${MONTH_FULL[Number(selectedMonth.value) - 1]} de ${selectedYear.value}`;
});

// Totais do ano OU do mês selecionado — para conferência direta com os KPIs
const monthlyTotals = computed(() => {
  const arr = isDailyView.value ? dailyEvolution.value : filteredMonthly.value;
  const total = arr.reduce((s, m) => s + (m.total || 0), 0);
  const resolved = arr.reduce((s, m) => s + (m.resolved || 0), 0);
  const handoff = arr.reduce((s, m) => s + (m.handoff || 0), 0);
  return { total, resolved, handoff };
});

const total = computed(() => d.value.total_conversations || 0);
const resolvedTotal = computed(() => d.value.total_resolved_count || 0);
const forcedClosure = computed(() => d.value.forced_closure_count || 0);
const openCount = computed(() => d.value.open_count || 0);
const pendingCount = computed(() => d.value.pending_count || 0);
const snoozedCount = computed(() => d.value.snoozed_count || 0);

const handoffDest = computed(
  () =>
    d.value.handoff_destination ?? {
      resolved: 0,
      open: 0,
      pending: 0,
      snoozed: 0,
    }
);
const handoffCount = computed(() => d.value.handoff_count || 0);
const handoffPct = computed(() =>
  total.value ? (handoffCount.value / total.value) * 100 : 0
);
const notHandoffCount = computed(() => total.value - handoffCount.value);

// aiResolvedDisplay: by exclusion so chips always sum to resolvedTotal exactly
const aiResolvedDisplay = computed(
  () => resolvedTotal.value - handoffDest.value.resolved - forcedClosure.value
);

// Resolved sub-widths (relative to resolvedTotal)
const aiResPct = computed(() =>
  resolvedTotal.value
    ? (aiResolvedDisplay.value / resolvedTotal.value) * 100
    : 0
);
const foResPct = computed(() =>
  resolvedTotal.value ? (forcedClosure.value / resolvedTotal.value) * 100 : 0
);
const huResPct = computed(() =>
  resolvedTotal.value
    ? (handoffDest.value.resolved / resolvedTotal.value) * 100
    : 0
);

const teamFunnel = computed(() => d.value.team_funnel ?? []);
const maxTeamCount = computed(
  () => teamFunnel.value.reduce((m, t) => Math.max(m, t.count), 0) || 1
);
const consultantKpis = computed(() => d.value.consultant_kpis ?? []);

// ── CSAT (separado em IA e Consultores) ─────────────────────────────────────
const csat = computed(() => d.value.csat ?? {});

function buildCsatBlock(block) {
  const b = block ?? {};
  const ratings = b.ratings_count ?? {};
  const totalResponses = b.total_responses || 0;
  const rows = [
    { emoji: '😍', label: 'Excelente', value: 5, color: 'bg-green-500' },
    { emoji: '😛', label: 'Bom', value: 4, color: 'bg-green-400' },
    { emoji: '😐', label: 'Mediano', value: 3, color: 'bg-yellow-400' },
    { emoji: '😑', label: 'Neutro', value: 2, color: 'bg-orange-400' },
    { emoji: '😞', label: 'Ruim', value: 1, color: 'bg-red-500' },
  ];
  return {
    total: totalResponses,
    score: b.satisfaction_score || 0,
    responseRate: b.response_rate || 0,
    distribution: rows.map(r => {
      const count = ratings[r.value] || 0;
      return {
        ...r,
        count,
        pct: totalResponses ? (count / totalResponses) * 100 : 0,
      };
    }),
  };
}

const csatIa = computed(() => buildCsatBlock(csat.value.ia));
const csatConsultant = computed(() => buildCsatBlock(csat.value.consultant));

const businessHoursCount = computed(() => d.value.business_hours_count ?? 0);
const offHoursCount = computed(() => d.value.off_hours_count ?? 0);
const totalForBadges = computed(
  () => businessHoursCount.value + offHoursCount.value || 1
);

// ── Charts ───────────────────────────────────────────────────────────────────
const dowLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const conversationsChart = computed(() => ({
  labels: dowLabels,
  datasets: [
    {
      label: 'Atendimentos',
      data: d.value.dow_conversations || Array(7).fill(0),
      backgroundColor: 'rgba(59,130,246,0.8)',
      borderRadius: 6,
      borderSkipped: false,
    },
    {
      label: 'Transbordos',
      data: d.value.dow_handoffs || Array(7).fill(0),
      backgroundColor: 'rgba(245,158,11,0.8)',
      borderRadius: 6,
      borderSkipped: false,
    },
  ],
}));

const resolvedChart = computed(() => ({
  labels: dowLabels,
  datasets: [
    {
      label: 'Resolvidos por IA',
      data: d.value.dow_ai_resolved || Array(7).fill(0),
      backgroundColor: 'rgba(16,185,129,0.8)',
      borderRadius: 6,
      borderSkipped: false,
    },
    {
      label: 'Resolvidos por Consultores',
      data: d.value.dow_human_resolved || Array(7).fill(0),
      backgroundColor: 'rgba(99,102,241,0.8)',
      borderRadius: 6,
      borderSkipped: false,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(15,23,42,0.9)',
      padding: 12,
      cornerRadius: 8,
      boxPadding: 4,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { precision: 0, font: { size: 11 } },
      grid: { color: 'rgba(0,0,0,0.04)' },
      border: { display: false },
    },
    x: {
      ticks: { font: { size: 11 } },
      grid: { display: false },
      border: { display: false },
    },
  },
};

// ── Evolução mensal (barras: total/concluídos + linha: taxa de transbordo) ──
// Fonte do gráfico: dia a dia (mês selecionado) ou mês a mês (ano inteiro)
const chartSource = computed(() =>
  isDailyView.value ? dailyEvolution.value : filteredMonthly.value
);

const monthlyChart = computed(() => ({
  labels: isDailyView.value
    ? dailyEvolution.value.map(row => row.day)
    : filteredMonthly.value.map(
        m => MONTH_NAMES[Number(m.month.split('/')[0]) - 1]
      ),
  datasets: [
    {
      type: 'bar',
      label: 'Atendimentos (total)',
      data: chartSource.value.map(row => row.total),
      backgroundColor: 'rgba(59,130,246,0.8)',
      borderRadius: 6,
      borderSkipped: false,
      yAxisID: 'y',
      order: 2,
    },
    {
      type: 'bar',
      label: 'Concluídos',
      data: chartSource.value.map(row => row.resolved),
      backgroundColor: 'rgba(16,185,129,0.8)',
      borderRadius: 6,
      borderSkipped: false,
      yAxisID: 'y',
      order: 2,
    },
    {
      type: 'line',
      label: 'Taxa de transbordo (%)',
      data: chartSource.value.map(row => row.handoff_rate),
      borderColor: 'rgba(245,158,11,1)',
      backgroundColor: 'rgba(245,158,11,0.2)',
      borderWidth: 2,
      tension: 0.3,
      pointRadius: isDailyView.value ? 2 : 4,
      pointBackgroundColor: 'rgba(245,158,11,1)',
      yAxisID: 'y1',
      order: 1,
    },
  ],
}));

// Clicar numa barra na visão mensal entra no drill-down diário daquele mês
function onChartClick(_evt, elements) {
  if (isDailyView.value || !elements.length) return;
  const idx = elements[0].index;
  const monthEntry = filteredMonthly.value[idx];
  if (monthEntry) selectMonth(monthEntry.month.split('/')[0]);
}

const monthlyChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: onChartClick,
  onHover: (evt, elements) => {
    const target = evt?.native?.target;
    if (target) {
      target.style.cursor =
        !isDailyView.value && elements.length ? 'pointer' : 'default';
    }
  },
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(15,23,42,0.9)',
      padding: 12,
      cornerRadius: 8,
      boxPadding: 4,
      callbacks: {
        title: items =>
          isDailyView.value ? `Dia ${items[0]?.label}` : items[0]?.label,
        label: ctx =>
          ctx.dataset.yAxisID === 'y1'
            ? `${ctx.dataset.label}: ${ctx.parsed.y}%`
            : `${ctx.dataset.label}: ${ctx.parsed.y}`,
      },
    },
  },
  scales: {
    y: {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      ticks: { precision: 0, font: { size: 11 } },
      grid: { color: 'rgba(0,0,0,0.04)' },
      border: { display: false },
    },
    y1: {
      type: 'linear',
      position: 'right',
      beginAtZero: true,
      max: 100,
      ticks: { callback: v => `${v}%`, font: { size: 11 } },
      grid: { drawOnChartArea: false },
      border: { display: false },
    },
    x: {
      ticks: { font: { size: 11 } },
      grid: { display: false },
      border: { display: false },
    },
  },
}));

// ── Exportar relatório (Planilha / PDF) ─────────────────────────────────────
const exportMenuOpen = ref(false);
const canExport = computed(() => !loading.value && !!data.value);

function formatBR(isoDate) {
  if (!isoDate) return '';
  const [y, m, dd] = isoDate.split('-');
  return `${dd}/${m}/${y}`;
}

const periodLabel = computed(
  () => `${formatBR(fromDate.value)} a ${formatBR(toDate.value)}`
);

// Estrutura única do "relatório geral" reaproveitada por planilha e PDF
function reportSections() {
  const v = d.value;
  const ratings = block => block?.ratings_count ?? {};
  const csatRows = block => [
    ['Total de respostas', block?.total_responses ?? 0],
    ['Pesquisas enviadas', block?.total_sent ?? 0],
    ['Pontuação de satisfação', `${block?.satisfaction_score ?? 0}%`],
    ['Taxa de resposta', `${block?.response_rate ?? 0}%`],
    ['Excelente (5)', ratings(block)[5] || 0],
    ['Bom (4)', ratings(block)[4] || 0],
    ['Mediano (3)', ratings(block)[3] || 0],
    ['Neutro (2)', ratings(block)[2] || 0],
    ['Ruim (1)', ratings(block)[1] || 0],
  ];

  return {
    overview: [
      ['Total de atendimentos', total.value],
      ['Em aberto', openCount.value],
      ['Pendentes', pendingCount.value],
      ['Adiadas', snoozedCount.value],
      ['Resolvidas (total)', resolvedTotal.value],
      ['Resolvidas pela IA', aiResolvedDisplay.value],
      ['Resolvidas por consultores', handoffDest.value.resolved],
      ['Encerramento forçado', forcedClosure.value],
    ],
    handoff: [
      ['Total de transbordos', handoffCount.value],
      ['Taxa de transbordo', `${handoffPct.value.toFixed(1)}%`],
      ['Tempo médio até transbordo', fmtSecs(v.avg_handoff_seconds)],
      ['Abandono após IA', v.abandonment_after_ai ?? 0],
      ['Abandono após consultor', v.abandonment_after_consultant ?? 0],
    ],
    tempos: [
      [
        'Mediana de atendimento (real)',
        fmtSecs(v.real_conversation_median_seconds),
      ],
      ['Tempo de resolução IA (mediana)', fmtSecs(v.ai_avg_resolution_seconds)],
      [
        'Tempo de resolução consultor (mediana)',
        fmtSecs(v.human_avg_resolution_seconds),
      ],
      ['Em horário comercial', businessHoursCount.value],
      ['Fora do horário', offHoursCount.value],
    ],
    csatIa: csatRows(csat.value.ia),
    csatConsultant: csatRows(csat.value.consultant),
    consultants: consultantKpis.value.map(k => [
      k.name,
      k.conversations_count,
      fmtSecs(k.avg_first_response_seconds),
      fmtSecs(k.avg_conversation_seconds),
    ]),
    teams: teamFunnel.value.map(t => [t.team, t.count]),
  };
}

// ── Planilha (.xlsx real → colunas corretas no Excel/Google Sheets) ──────────
const bold = v => ({ v, bold: true });

function exportSpreadsheet() {
  exportMenuOpen.value = false;
  if (!canExport.value) return;
  const s = reportSections();
  const rows = [
    [bold('Relatório — Dashboard IA')],
    ['Período', periodLabel.value],
    ['Gerado em', new Date().toLocaleString('pt-BR')],
    [],
    [bold('VISÃO GERAL')],
    [bold('Métrica'), bold('Valor')],
    ...s.overview,
    [],
    [bold('TRANSBORDOS')],
    [bold('Métrica'), bold('Valor')],
    ...s.handoff,
    [],
    [bold('TEMPOS E HORÁRIOS')],
    [bold('Métrica'), bold('Valor')],
    ...s.tempos,
    [],
    [bold('CSAT — IA')],
    [bold('Métrica'), bold('Valor')],
    ...s.csatIa,
    [],
    [bold('CSAT — CONSULTORES')],
    [bold('Métrica'), bold('Valor')],
    ...s.csatConsultant,
    [],
    [bold('CONSULTORES')],
    [
      bold('Consultor'),
      bold('Atendimentos'),
      bold('1ª resposta (mediana)'),
      bold('Duração média'),
    ],
    ...s.consultants,
  ];
  if (s.teams.length) {
    rows.push(
      [],
      [bold('EQUIPES (TRANSBORDO)')],
      [bold('Equipe'), bold('Transbordos')],
      ...s.teams
    );
  }
  downloadXlsxFile(
    `dashboard-ia-${fromDate.value}_a_${toDate.value}.xlsx`,
    rows,
    { sheetName: 'Dashboard IA' }
  );
}

// ── PDF (relatório HTML formatado → diálogo de impressão "Salvar como PDF") ──
function exportPdf() {
  exportMenuOpen.value = false;
  if (!canExport.value) return;
  const s = reportSections();
  const win = window.open('', '_blank');
  if (!win) return;

  const esc = x =>
    String(x ?? '').replace(
      /[&<>]/g,
      ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[ch]
    );
  const kv = pairs =>
    `<table class="kv">${pairs
      .map(([k, val]) => `<tr><td>${esc(k)}</td><td>${esc(val)}</td></tr>`)
      .join('')}</table>`;
  const card = (title, body) =>
    `<section class="card"><h2>${esc(title)}</h2>${body}</section>`;
  const hero = [
    ['Atendimentos', total.value],
    ['Resolvidas pela IA', aiResolvedDisplay.value],
    ['Transbordos', handoffCount.value],
    ['CSAT IA', `${csat.value.ia?.satisfaction_score ?? 0}%`],
    ['CSAT Consultores', `${csat.value.consultant?.satisfaction_score ?? 0}%`],
  ]
    .map(
      ([k, val]) =>
        `<div class="hero"><span class="hv">${esc(val)}</span><span class="hk">${esc(k)}</span></div>`
    )
    .join('');
  const consultantsTable = s.consultants.length
    ? `<table class="grid"><thead><tr><th>Consultor</th><th>Atendimentos</th><th>1ª resposta</th><th>Duração média</th></tr></thead><tbody>${s.consultants
        .map(
          r =>
            `<tr><td>${esc(r[0])}</td><td>${esc(r[1])}</td><td>${esc(r[2])}</td><td>${esc(r[3])}</td></tr>`
        )
        .join('')}</tbody></table>`
    : '<p class="empty">Sem dados de consultores no período.</p>';

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8" />
<title>Relatório Dashboard IA — ${esc(periodLabel.value)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; margin: 0; background: #f1f5f9; }
  .page { max-width: 900px; margin: 0 auto; padding: 24px; }
  .topbar { background: linear-gradient(135deg, #6366f1, #2563eb); color: #fff; border-radius: 16px; padding: 28px 32px; }
  .topbar h1 { margin: 0; font-size: 24px; letter-spacing: -.3px; }
  .topbar p { margin: 6px 0 0; opacity: .9; font-size: 13px; }
  .heroes { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin: 18px 0 4px; }
  .hero { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; text-align: center; }
  .hv { display: block; font-size: 22px; font-weight: 700; color: #0f172a; }
  .hk { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #64748b; margin-top: 4px; }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 20px; margin-top: 16px; }
  .card h2 { margin: 0 0 12px; font-size: 13px; text-transform: uppercase; letter-spacing: .5px; color: #475569; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .kv td { padding: 7px 0; border-bottom: 1px solid #f1f5f9; }
  .kv td:last-child { text-align: right; font-weight: 600; color: #0f172a; white-space: nowrap; }
  .grid th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; color: #64748b; padding: 8px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
  .grid td { padding: 8px; border-bottom: 1px solid #f1f5f9; }
  .grid td:not(:first-child) { text-align: right; }
  .empty { color: #94a3b8; font-size: 13px; }
  .foot { text-align: center; color: #94a3b8; font-size: 11px; margin: 20px 0 4px; }
  .toolbar { text-align: center; margin: 18px 0; }
  .toolbar button { background: #2563eb; color: #fff; border: 0; border-radius: 10px; padding: 10px 22px; font-size: 14px; font-weight: 600; cursor: pointer; }
  @media print { body { background: #fff; } .no-print { display: none; } .page { padding: 0; } @page { size: A4; margin: 14mm; } }
</style></head>
<body><div class="page">
  <div class="topbar"><h1>Relatório — Dashboard IA</h1><p>Período: ${esc(periodLabel.value)} &nbsp;•&nbsp; Gerado em ${esc(new Date().toLocaleString('pt-BR'))}</p></div>
  <div class="toolbar no-print"><button onclick="window.print()">Imprimir / Salvar como PDF</button></div>
  <div class="heroes">${hero}</div>
  <div class="cols">${card('Visão geral', kv(s.overview))}${card('Transbordos', kv(s.handoff))}</div>
  ${card('Tempos e horários', kv(s.tempos))}
  <div class="cols">${card('CSAT — IA', kv(s.csatIa))}${card('CSAT — Consultores', kv(s.csatConsultant))}</div>
  ${card('Consultores', consultantsTable)}
  <p class="foot">Sky Intelligence Chat — Relatório gerado automaticamente</p>
</div></body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    win.focus();
    win.print();
  }, 350);
}
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <div
      class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h1 class="text-2xl font-bold tracking-tight text-n-slate-12">
          Dashboard IA
        </h1>
        <p class="mt-1 text-sm text-n-slate-10">
          Performance da IA, transbordos e consultores
        </p>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          class="flex items-center gap-2.5 rounded-xl border border-n-weak bg-white px-4 py-2.5 shadow-sm dark:bg-n-alpha-2"
        >
          <span class="i-lucide-calendar-days h-4 w-4 text-n-slate-10" />
          <input
            v-model="fromDate"
            type="date"
            class="w-[130px] bg-transparent text-sm font-medium text-n-slate-12 outline-none"
          />
          <span class="text-n-slate-8">→</span>
          <input
            v-model="toDate"
            type="date"
            class="w-[130px] bg-transparent text-sm font-medium text-n-slate-12 outline-none"
          />
        </div>

        <!-- Exportar relatório (Planilha / PDF) -->
        <div class="relative">
          <button
            type="button"
            :disabled="!canExport"
            class="flex w-full items-center justify-center gap-2 rounded-xl border border-n-weak bg-white px-4 py-2.5 text-sm font-semibold text-n-slate-12 shadow-sm transition hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-n-alpha-2 sm:w-auto"
            @click="exportMenuOpen = !exportMenuOpen"
          >
            <span class="i-lucide-download h-4 w-4 text-n-slate-10" />
            Exportar
            <span
              class="i-lucide-chevron-down h-3.5 w-3.5 text-n-slate-9 transition-transform"
              :class="exportMenuOpen ? 'rotate-180' : ''"
            />
          </button>

          <!-- backdrop p/ fechar ao clicar fora -->
          <div
            v-if="exportMenuOpen"
            class="fixed inset-0 z-10"
            @click="exportMenuOpen = false"
          />

          <!-- menu -->
          <div
            v-if="exportMenuOpen"
            class="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-xl border border-n-weak bg-white shadow-lg dark:bg-n-alpha-2"
          >
            <button
              type="button"
              class="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-n-alpha-1"
              @click="exportSpreadsheet"
            >
              <span
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-500/10"
              >
                <span
                  class="i-lucide-file-spreadsheet h-4 w-4 text-green-600"
                />
              </span>
              <span>
                <span class="block text-sm font-semibold text-n-slate-12"
                  >Planilha</span
                >
                <span class="block text-[11px] text-n-slate-9"
                  >Excel / CSV — dados completos</span
                >
              </span>
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-3 border-t border-n-weak px-4 py-3 text-left transition hover:bg-n-alpha-1"
              @click="exportPdf"
            >
              <span
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-500/10"
              >
                <span class="i-lucide-file-text h-4 w-4 text-red-600" />
              </span>
              <span>
                <span class="block text-sm font-semibold text-n-slate-12"
                  >PDF</span
                >
                <span class="block text-[11px] text-n-slate-9"
                  >Relatório formatado para impressão</span
                >
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Loading ────────────────────────────────────────────────────────── -->
    <div v-if="loading" class="flex flex-col gap-4">
      <div class="h-48 animate-pulse rounded-xl bg-n-alpha-3" />
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div
          v-for="i in 4"
          :key="i"
          class="h-28 animate-pulse rounded-xl bg-n-alpha-3"
        />
      </div>
    </div>

    <!-- ── Error ──────────────────────────────────────────────────────────── -->
    <div
      v-else-if="error"
      class="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700"
    >
      <span class="i-lucide-alert-circle h-5 w-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button
        class="ml-auto rounded-lg px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
        @click="fetchDashboard"
      >
        Tentar novamente
      </button>
    </div>

    <template v-else-if="data">
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- KPIs TOTAIS — DO PERÍODO (respeita o filtro de datas)            -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-xl bg-n-brand"
            >
              <span class="i-lucide-bar-chart-3 h-4 w-4 text-white" />
            </span>
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                Totais do período
              </h2>
              <p class="text-[11px] text-n-slate-9">
                De acordo com o filtro de datas selecionado
              </p>
            </div>
          </div>
          <span
            class="rounded-full bg-n-alpha-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-n-slate-10"
          >
            Filtro aplicado
          </span>
        </div>

        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <!-- Atendimentos total -->
          <div
            class="relative overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div
              class="absolute left-0 top-0 h-1 w-full bg-woot-500 opacity-70"
            />
            <div class="flex items-start justify-between">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Atendimentos (total)
              </p>
              <span
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-woot-100 dark:bg-woot-500/15"
              >
                <span
                  class="i-lucide-message-circle h-4 w-4 text-woot-600 dark:text-woot-400"
                />
              </span>
            </div>
            <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ totals.conversations }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Todas as conversas do período
            </p>
          </div>

          <!-- Tempo médio total -->
          <div
            class="relative overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div
              class="absolute left-0 top-0 h-1 w-full bg-slate-400 opacity-70"
            />
            <div class="flex items-start justify-between">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Tempo médio (total)
              </p>
              <span
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-500/15"
              >
                <span
                  class="i-lucide-clock h-4 w-4 text-slate-500 dark:text-slate-400"
                />
              </span>
            </div>
            <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ fmtSecs(totals.avgSeconds) }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Mediana da duração real da conversa
            </p>
          </div>

          <!-- CSAT total -->
          <div
            class="relative overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div
              class="absolute left-0 top-0 h-1 w-full bg-green-500 opacity-70"
            />
            <div class="flex items-start justify-between">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                CSAT (total)
              </p>
              <span
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 dark:bg-green-500/15"
              >
                <span
                  class="i-lucide-smile h-4 w-4 text-green-600 dark:text-green-400"
                />
              </span>
            </div>
            <p class="mt-3 text-2xl font-bold tabular-nums text-green-500">
              {{ totals.csatScore }}%
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Satisfação (notas 4 e 5) do período
            </p>
          </div>

          <!-- Taxa de transbordo total -->
          <div
            class="relative overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div
              class="absolute left-0 top-0 h-1 w-full bg-violet-500 opacity-70"
            />
            <div class="flex items-start justify-between">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Taxa de transbordo (total)
              </p>
              <span
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-500/15"
              >
                <span
                  class="i-lucide-arrow-right-left h-4 w-4 text-violet-600 dark:text-violet-400"
                />
              </span>
            </div>
            <p class="mt-3 text-2xl font-bold tabular-nums text-violet-500">
              {{ totals.handoffRate }}%
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Conversas que precisaram de consultor
            </p>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 1 — DESTINO DAS CONVERSAS (sempre fecha em 100%)           -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <!-- Header -->
        <div class="mb-5 flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-xl bg-green-500"
            >
              <span class="i-lucide-pie-chart h-4 w-4 text-white" />
            </span>
            <h2 class="text-sm font-semibold text-n-slate-12">
              Destino das Conversas
            </h2>
          </div>
          <div class="text-right">
            <span class="text-3xl font-bold tabular-nums text-n-slate-12">{{
              total
            }}</span>
            <p class="text-[11px] text-n-slate-9">conversas totais</p>
          </div>
        </div>

        <!-- Cards de status -->
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <!-- Resolvidas -->
          <div class="overflow-hidden rounded-xl">
            <div
              class="flex items-center justify-between bg-green-500 px-4 py-2"
            >
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
              >
                <span class="i-lucide-check-circle-2 h-3.5 w-3.5" />Resolvidas
              </span>
              <span class="text-[11px] font-bold text-white/80">{{
                pct(resolvedTotal, total)
              }}</span>
            </div>
            <div class="bg-n-alpha-2 p-4">
              <p class="text-3xl font-bold tabular-nums text-green-500">
                {{ resolvedTotal }}
              </p>
              <div
                class="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-n-alpha-3"
              >
                <div
                  class="bg-green-500 transition-all duration-700"
                  :style="{ width: huResPct + '%' }"
                />
                <div
                  class="bg-n-teal-4 transition-all duration-700"
                  :style="{ width: aiResPct + '%' }"
                />
                <div
                  class="bg-red-500/15 transition-all duration-700"
                  :style="{ width: foResPct + '%' }"
                />
              </div>
              <div class="mt-3 flex flex-col gap-1.5">
                <div
                  class="flex items-center gap-1.5 rounded-lg bg-n-teal-4 px-2 py-1.5"
                >
                  <span class="i-lucide-bot h-3 w-3 text-n-teal-10" />
                  <span class="flex-1 text-[11px] font-semibold text-n-teal-10"
                    >IA sozinha</span
                  >
                  <span class="text-xs font-bold tabular-nums text-n-teal-10">{{
                    aiResolvedDisplay
                  }}</span>
                </div>
                <div
                  class="flex items-center gap-1.5 rounded-lg bg-red-500/15 px-2 py-1.5"
                >
                  <span class="i-lucide-x-circle h-3 w-3 text-red-400" />
                  <span class="flex-1 text-[11px] font-semibold text-red-400"
                    >Forçadas</span
                  >
                  <span class="text-xs font-bold tabular-nums text-red-400">{{
                    forcedClosure
                  }}</span>
                </div>
                <div
                  class="flex items-center gap-1.5 rounded-lg bg-green-500/15 px-2 py-1.5"
                >
                  <span class="i-lucide-user h-3 w-3 text-green-500" />
                  <span class="flex-1 text-[11px] font-semibold text-green-500"
                    >Com consultor</span
                  >
                  <span class="text-xs font-bold tabular-nums text-green-500">{{
                    handoffDest.resolved
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Abertas -->
          <div class="overflow-hidden rounded-xl">
            <div
              class="flex items-center justify-between bg-woot-500 px-4 py-2"
            >
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
              >
                <span class="i-lucide-message-circle h-3.5 w-3.5" />Abertas
                agora
              </span>
              <span class="text-[11px] font-bold text-white/80">{{
                pct(openCount, total)
              }}</span>
            </div>
            <div class="bg-n-alpha-2 p-4">
              <p class="text-3xl font-bold tabular-nums text-woot-500">
                {{ openCount }}
              </p>
              <p class="mt-3 text-[11px] text-n-slate-9">
                Conversas em atendimento ativo
              </p>
            </div>
          </div>

          <!-- Pendentes -->
          <div class="overflow-hidden rounded-xl">
            <div
              class="flex items-center justify-between bg-orange-500 px-4 py-2"
            >
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
              >
                <span class="i-lucide-clock h-3.5 w-3.5" />Pendentes
              </span>
              <span class="text-[11px] font-bold text-white/80">{{
                pct(pendingCount, total)
              }}</span>
            </div>
            <div class="bg-n-alpha-2 p-4">
              <p class="text-3xl font-bold tabular-nums text-orange-500">
                {{ pendingCount }}
              </p>
              <p class="mt-3 text-[11px] text-n-slate-9">
                Em atendimento pela IA — ainda não resolvidas
              </p>
            </div>
          </div>

          <!-- Adiadas -->
          <div class="overflow-hidden rounded-xl">
            <div
              class="flex items-center justify-between bg-violet-500 px-4 py-2"
            >
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
              >
                <span class="i-lucide-alarm-clock h-3.5 w-3.5" />Adiadas
              </span>
              <span class="text-[11px] font-bold text-white/80">{{
                pct(snoozedCount, total)
              }}</span>
            </div>
            <div class="bg-n-alpha-2 p-4">
              <p class="text-3xl font-bold tabular-nums text-violet-500">
                {{ snoozedCount }}
              </p>
              <p class="mt-3 text-[11px] text-n-slate-9">
                Conversas pausadas para retomar depois
              </p>
            </div>
          </div>
        </div>

        <!-- Faixa de resumo colorida por coluna -->
        <div class="mt-4 grid grid-cols-4 gap-px overflow-hidden rounded-xl">
          <div class="flex flex-col items-center gap-0.5 bg-green-500 py-3">
            <span class="text-lg font-bold tabular-nums text-white">{{
              resolvedTotal
            }}</span>
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-white/80"
              >Resolvidas</span
            >
            <span class="text-[11px] font-semibold text-white/70">{{
              pct(resolvedTotal, total)
            }}</span>
          </div>
          <div class="flex flex-col items-center gap-0.5 bg-woot-500 py-3">
            <span class="text-lg font-bold tabular-nums text-white">{{
              openCount
            }}</span>
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-white/80"
              >Abertas</span
            >
            <span class="text-[11px] font-semibold text-white/70">{{
              pct(openCount, total)
            }}</span>
          </div>
          <div class="flex flex-col items-center gap-0.5 bg-orange-500 py-3">
            <span class="text-lg font-bold tabular-nums text-white">{{
              pendingCount
            }}</span>
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-white/80"
              >Pendentes</span
            >
            <span class="text-[11px] font-semibold text-white/70">{{
              pct(pendingCount, total)
            }}</span>
          </div>
          <div class="flex flex-col items-center gap-0.5 bg-violet-500 py-3">
            <span class="text-lg font-bold tabular-nums text-white">{{
              snoozedCount
            }}</span>
            <span
              class="text-[10px] font-bold uppercase tracking-wider text-white/80"
              >Adiadas</span
            >
            <span class="text-[11px] font-semibold text-white/70">{{
              pct(snoozedCount, total)
            }}</span>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO CSAT — SATISFAÇÃO DO CLIENTE (IA × CONSULTORES)            -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <!-- CSAT — IA -->
        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <!-- Header -->
          <div class="mb-5 flex items-center gap-2.5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-500"
            >
              <span class="i-lucide-sparkles h-4 w-4 text-white" />
            </span>
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">CSAT — IA</h2>
              <p class="text-[11px] text-n-slate-9">
                Avaliações de conversas atendidas pela IA
              </p>
            </div>
          </div>

          <!-- KPIs -->
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Total de respostas
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-n-slate-12">
                {{ csatIa.total }}
              </p>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Pontuação de satisfação
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-green-500">
                {{ csatIa.score }}%
              </p>
              <p class="mt-1 text-[11px] text-n-slate-9">
                Avaliações boas e excelentes (4 e 5)
              </p>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Taxa de resposta
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-woot-500">
                {{ csatIa.responseRate }}%
              </p>
              <p class="mt-1 text-[11px] text-n-slate-9">
                Respostas sobre pesquisas enviadas
              </p>
            </div>
          </div>

          <!-- Distribuição das avaliações -->
          <div class="mt-5">
            <p
              class="mb-3 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Distribuição das avaliações
            </p>
            <div class="flex flex-col gap-2.5">
              <div
                v-for="r in csatIa.distribution"
                :key="r.value"
                class="flex items-center gap-3"
              >
                <span
                  class="flex w-28 shrink-0 items-center gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  <span class="text-base leading-none">{{ r.emoji }}</span
                  >{{ r.label }}
                </span>
                <div
                  class="h-2.5 flex-1 overflow-hidden rounded-full bg-n-alpha-3"
                >
                  <div
                    class="h-2.5 rounded-full transition-all duration-700"
                    :class="r.color"
                    :style="{ width: r.pct + '%' }"
                  />
                </div>
                <span
                  class="w-20 shrink-0 text-right text-xs tabular-nums text-n-slate-11"
                >
                  {{ r.pct.toFixed(0) }}%
                  <span class="text-n-slate-9">({{ r.count }})</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- CSAT — Consultores -->
        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <!-- Header -->
          <div class="mb-5 flex items-center gap-2.5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-xl bg-sky-500"
            >
              <span class="i-lucide-users h-4 w-4 text-white" />
            </span>
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                CSAT — Consultores
              </h2>
              <p class="text-[11px] text-n-slate-9">
                Avaliações de conversas atendidas por consultores
              </p>
            </div>
          </div>

          <!-- KPIs -->
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Total de respostas
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-n-slate-12">
                {{ csatConsultant.total }}
              </p>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Pontuação de satisfação
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-green-500">
                {{ csatConsultant.score }}%
              </p>
              <p class="mt-1 text-[11px] text-n-slate-9">
                Avaliações boas e excelentes (4 e 5)
              </p>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                Taxa de resposta
              </p>
              <p class="mt-2 text-3xl font-bold tabular-nums text-woot-500">
                {{ csatConsultant.responseRate }}%
              </p>
              <p class="mt-1 text-[11px] text-n-slate-9">
                Respostas sobre pesquisas enviadas
              </p>
            </div>
          </div>

          <!-- Distribuição das avaliações -->
          <div class="mt-5">
            <p
              class="mb-3 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Distribuição das avaliações
            </p>
            <div class="flex flex-col gap-2.5">
              <div
                v-for="r in csatConsultant.distribution"
                :key="r.value"
                class="flex items-center gap-3"
              >
                <span
                  class="flex w-28 shrink-0 items-center gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  <span class="text-base leading-none">{{ r.emoji }}</span
                  >{{ r.label }}
                </span>
                <div
                  class="h-2.5 flex-1 overflow-hidden rounded-full bg-n-alpha-3"
                >
                  <div
                    class="h-2.5 rounded-full transition-all duration-700"
                    :class="r.color"
                    :style="{ width: r.pct + '%' }"
                  />
                </div>
                <span
                  class="w-20 shrink-0 text-right text-xs tabular-nums text-n-slate-11"
                >
                  {{ r.pct.toFixed(0) }}%
                  <span class="text-n-slate-9">({{ r.count }})</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 2 — TRANSBORDADAS: quantas precisaram de consultor humano  -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div class="mb-5 flex items-center gap-2.5">
          <span
            class="flex h-8 w-8 items-center justify-center rounded-xl bg-n-alpha-3"
          >
            <span class="i-lucide-git-compare h-4 w-4 text-n-slate-10" />
          </span>
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              IA vs Consultor — divisão do atendimento
            </h2>
            <p class="text-[11px] text-n-slate-9">
              Das {{ total }} conversas, quantas a IA resolveu sozinha e quantas
              precisaram de um consultor humano
            </p>
          </div>
        </div>

        <!-- Divisão principal: IA sozinha vs Com consultor -->
        <div class="grid grid-cols-2 gap-3">
          <!-- IA sozinha -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-2 p-4 dark:bg-n-alpha-3"
          >
            <div class="flex items-center gap-2 mb-3">
              <span
                class="flex h-7 w-7 items-center justify-center rounded-lg bg-n-alpha-3"
              >
                <span class="i-lucide-bot h-3.5 w-3.5 text-n-teal-9" />
              </span>
              <p
                class="text-[11px] font-bold uppercase tracking-wider text-n-slate-11"
              >
                IA sem consultor
              </p>
            </div>
            <p class="text-4xl font-bold tabular-nums text-n-slate-12">
              {{ notHandoffCount }}
            </p>
            <p class="mt-1 text-sm font-semibold text-n-slate-10">
              {{ total ? ((notHandoffCount / total) * 100).toFixed(1) : 0 }}%
              das conversas
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              A IA atendeu sem acionar nenhum consultor
            </p>

            <div class="mt-4 flex flex-col gap-1.5">
              <p
                class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
              >
                Situação atual
              </p>
              <div
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span class="i-lucide-check-circle h-3 w-3 text-n-teal-9" />IA
                  resolveu
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  aiResolvedDisplay
                }}</span>
              </div>
              <div
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span
                    class="i-lucide-x-circle h-3 w-3 text-red-500"
                  />Forçadas (sem resposta)
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  forcedClosure
                }}</span>
              </div>
              <div
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span class="i-lucide-clock h-3 w-3 text-orange-500" />Em
                  atendimento pela IA
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  pendingCount - handoffDest.pending
                }}</span>
              </div>
            </div>
          </div>

          <!-- Com consultor (transbordadas) -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-2 p-4 dark:bg-n-alpha-3"
          >
            <div class="flex items-center gap-2 mb-3">
              <span
                class="flex h-7 w-7 items-center justify-center rounded-lg bg-n-alpha-3"
              >
                <span
                  class="i-lucide-arrow-right-left h-3.5 w-3.5 text-violet-500"
                />
              </span>
              <p
                class="text-[11px] font-bold uppercase tracking-wider text-n-slate-11"
              >
                Precisaram de consultor
              </p>
            </div>
            <p class="text-4xl font-bold tabular-nums text-n-slate-12">
              {{ handoffCount }}
            </p>
            <p class="mt-1 text-sm font-semibold text-n-slate-10">
              {{ handoffPct.toFixed(1) }}% das conversas
            </p>

            <!-- O que aconteceu com as transbordadas -->
            <div class="mt-4 flex flex-col gap-1.5">
              <p
                class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
              >
                O que aconteceu depois
              </p>
              <div
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span
                    class="i-lucide-check-circle h-3 w-3 text-green-500"
                  />Consultor resolveu
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  handoffDest.resolved
                }}</span>
              </div>
              <div
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span
                    class="i-lucide-message-circle h-3 w-3 text-woot-500"
                  />Ainda em atendimento
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  handoffDest.open
                }}</span>
              </div>
              <div
                v-if="handoffDest.snoozed > 0"
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span
                    class="i-lucide-alarm-clock h-3 w-3 text-violet-500"
                  />Adiadas pelo consultor
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  handoffDest.snoozed
                }}</span>
              </div>
              <div
                v-if="handoffDest.pending > 0"
                class="flex items-center justify-between rounded-lg bg-n-alpha-3 px-3 py-2"
              >
                <span
                  class="flex items-center gap-1.5 text-[11px] font-semibold text-n-slate-11"
                >
                  <span
                    class="i-lucide-clock h-3 w-3 text-orange-500"
                  />Pendentes
                </span>
                <span class="text-sm font-bold tabular-nums text-n-slate-12">{{
                  handoffDest.pending
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 3 — SAÚDE DO ATENDIMENTO (overlays / análise de fluxo)     -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div class="mb-4 flex items-center gap-2">
          <span
            class="flex h-7 w-7 items-center justify-center rounded-lg bg-red-100 dark:bg-red-500/15"
          >
            <span
              class="i-lucide-activity h-3.5 w-3.5 text-red-600 dark:text-red-400"
            />
          </span>
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              Saúde do Atendimento
            </h2>
            <p class="text-[11px] text-n-slate-9">
              Alertas de abandono e tempos de resposta
            </p>
          </div>
        </div>

        <!-- Abandono: dois cards lado a lado com contexto claro -->
        <div class="mb-3 grid grid-cols-2 gap-3">
          <!-- Abandono após IA -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <div class="flex items-center justify-between mb-3">
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
              >
                <span class="i-lucide-bot-off h-3.5 w-3.5 text-red-500" />
                Sem resposta — IA esperando
              </span>
              <span
                class="rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white"
              >
                {{
                  pendingCount - handoffDest.pending
                    ? (
                        ((d.abandonment_after_ai || 0) /
                          (pendingCount - handoffDest.pending)) *
                        100
                      ).toFixed(0)
                    : 0
                }}%
              </span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-n-slate-12">
              {{ d.abandonment_after_ai }}
            </p>
            <p class="mt-1 text-xs text-n-slate-9">
              de {{ pendingCount - handoffDest.pending }} pendentes (IA) — parte
              das {{ total }} totais
            </p>
            <p class="mt-2 text-[11px] text-n-slate-9">
              Pendentes onde a IA enviou a última mensagem e o cliente não
              respondeu há 2+ dias.
            </p>
          </div>

          <!-- Abandono após Consultor -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <div class="flex items-center justify-between mb-3">
              <span
                class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
              >
                <span class="i-lucide-user-x h-3.5 w-3.5 text-yellow-500" />
                Sem resposta — Consultor esperando
              </span>
              <span
                class="rounded-full bg-yellow-500 px-2 py-0.5 text-[11px] font-bold text-white"
              >
                {{
                  openCount
                    ? (
                        ((d.abandonment_after_consultant || 0) / openCount) *
                        100
                      ).toFixed(0)
                    : 0
                }}%
              </span>
            </div>
            <p class="text-3xl font-bold tabular-nums text-n-slate-12">
              {{ d.abandonment_after_consultant }}
            </p>
            <p class="mt-1 text-xs text-n-slate-9">
              de {{ openCount }} abertas (consultor) — parte das
              {{ total }} totais
            </p>
            <p class="mt-2 text-[11px] text-n-slate-9">
              Abertas onde o consultor enviou a última mensagem e o cliente não
              respondeu há 2+ dias.
            </p>
          </div>
        </div>

        <!-- Tempos e distribuição -->
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <!-- Tempo médio de transbordo -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <div class="flex items-center gap-1.5 mb-3">
              <span class="i-lucide-timer h-3.5 w-3.5 text-violet-500" />
              <p
                class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
              >
                Tempo até Transbordo
              </p>
            </div>
            <p class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ fmtSecs(d.avg_handoff_seconds) }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Mediana desde o início da conversa até a IA acionar um consultor
            </p>
          </div>

          <!-- Duração real -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <div class="flex items-center gap-1.5 mb-3">
              <span class="i-lucide-clock h-3.5 w-3.5 text-woot-500" />
              <p
                class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
              >
                Duração da Conversa
              </p>
            </div>
            <p class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ fmtSecs(d.real_conversation_median_seconds) }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-9">
              Mediana entre 1ª e última mensagem real do período
            </p>
          </div>

          <!-- Horário comercial -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <div class="flex items-center gap-1.5 mb-3">
              <span class="i-lucide-sun h-3.5 w-3.5 text-yellow-500" />
              <p
                class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
              >
                Horário Comercial
              </p>
            </div>
            <p class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ businessHoursCount }}
            </p>
            <div class="mt-2 flex items-center gap-2">
              <div
                class="h-1.5 flex-1 overflow-hidden rounded-full bg-n-alpha-3"
              >
                <div
                  class="h-1.5 rounded-full bg-green-500"
                  :style="{
                    width: `${(businessHoursCount / totalForBadges) * 100}%`,
                  }"
                />
              </div>
              <span class="text-[11px] text-n-slate-9"
                >{{ ((businessHoursCount / totalForBadges) * 100).toFixed(0) }}%
                Seg–Sex 9h–18h</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 3 — PERFORMANCE DA IA                                      -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-n-teal-4 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Taxa de Conclusão IA
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-n-teal-4 dark:bg-n-teal-4"
            >
              <span
                class="i-lucide-sparkles h-4 w-4 text-n-teal-11 dark:text-n-teal-10"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{
              resolvedTotal
                ? ((aiResolvedDisplay / resolvedTotal) * 100).toFixed(1)
                : 0
            }}%
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            {{ aiResolvedDisplay }} de {{ resolvedTotal }} resolvidas sem
            precisar de humano
          </p>
        </div>

        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-n-teal-9 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Tempo de Conclusão IA
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-n-teal-4 dark:bg-n-teal-4"
            >
              <span
                class="i-lucide-zap h-4 w-4 text-n-teal-11 dark:text-n-teal-10"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{ fmtSecs(d.ai_avg_resolution_seconds) }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            Mediana do atendimento resolvido só pela IA
          </p>
        </div>

        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-slate-400 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Duração Real da Conversa
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-500/15"
            >
              <span
                class="i-lucide-clock h-4 w-4 text-slate-500 dark:text-slate-400"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{ fmtSecs(d.real_conversation_median_seconds) }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            Mediana entre 1ª e última mensagem real
          </p>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 3B — PERFORMANCE DO CONSULTOR                              -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <!-- Taxa de Conclusão Consultor -->
        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-violet-300 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Taxa de Conclusão Consultor
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-500/15"
            >
              <span
                class="i-lucide-user-check h-4 w-4 text-violet-600 dark:text-violet-400"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{
              resolvedTotal
                ? ((handoffDest.resolved / resolvedTotal) * 100).toFixed(1)
                : 0
            }}%
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            {{ handoffDest.resolved }} de {{ resolvedTotal }} resolvidas com
            ajuda de consultor
          </p>
        </div>

        <!-- Tempo de Conclusão Consultor -->
        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-violet-500 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Tempo de Conclusão Consultor
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-500/15"
            >
              <span
                class="i-lucide-zap h-4 w-4 text-violet-600 dark:text-violet-400"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{ fmtSecs(d.human_avg_resolution_seconds) }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            Mediana do atendimento resolvido com consultor
          </p>
        </div>

        <!-- Duração Real da Conversa (com consultor) -->
        <div
          class="relative overflow-hidden rounded-xl border border-n-weak bg-white p-4 dark:bg-n-alpha-2"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full bg-violet-400 opacity-60"
          />
          <div class="flex items-start justify-between">
            <p
              class="text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Duração Real da Conversa
            </p>
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-500/15"
            >
              <span
                class="i-lucide-clock h-4 w-4 text-violet-600 dark:text-violet-400"
              />
            </span>
          </div>
          <p class="mt-3 text-2xl font-bold tabular-nums text-n-slate-12">
            {{ fmtSecs(d.human_conversation_median_seconds) }}
          </p>
          <p class="mt-1 text-[11px] text-n-slate-9">
            Mediana entre 1ª e última mensagem real (com consultor)
          </p>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 4 — FUNIL DE TRANSBORDOS POR TIME                          -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        v-if="teamFunnel.length > 0"
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div class="mb-4 flex items-center gap-2">
          <span
            class="flex h-7 w-7 items-center justify-center rounded-lg bg-n-brand/10"
          >
            <span class="i-lucide-git-branch h-3.5 w-3.5 text-n-brand" />
          </span>
          <h2 class="text-sm font-semibold text-n-slate-12">
            Transbordos por Time
          </h2>
        </div>
        <div class="flex flex-col gap-3">
          <div v-for="item in teamFunnel" :key="item.team">
            <div class="mb-1 flex items-center justify-between text-xs">
              <span class="font-medium text-n-slate-12">{{
                item.team || 'Sem time'
              }}</span>
              <div class="flex items-center gap-2">
                <span class="font-bold tabular-nums text-n-slate-12">{{
                  item.count
                }}</span>
                <span
                  class="rounded bg-n-alpha-3 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-10"
                >
                  {{
                    ((item.count / (d.handoff_count || 1)) * 100).toFixed(1)
                  }}%
                </span>
              </div>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-n-alpha-3">
              <div
                class="h-2 rounded-full bg-n-brand transition-all duration-700"
                :style="{ width: `${(item.count / maxTeamCount) * 100}%` }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 5 — GRÁFICOS                                               -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <h2
            class="mb-1 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-woot-100 dark:bg-woot-500/15"
            >
              <span
                class="i-lucide-bar-chart-3 h-3.5 w-3.5 text-woot-600 dark:text-woot-400"
              />
            </span>
            Atendimentos e Transbordos
          </h2>
          <p class="mb-4 text-[11px] text-n-slate-9">
            Distribuição por dia da semana
          </p>
          <div class="h-64">
            <Bar :data="conversationsChart" :options="chartOptions" />
          </div>
        </div>

        <div
          class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
        >
          <h2
            class="mb-1 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-green-500/15 dark:bg-green-500/15"
            >
              <span
                class="i-lucide-bar-chart-3 h-3.5 w-3.5 text-green-600 dark:text-green-400"
              />
            </span>
            Resolvidos por IA vs Consultores
          </h2>
          <p class="mb-4 text-[11px] text-n-slate-9">
            Distribuição por dia da semana
          </p>
          <div class="h-64">
            <Bar :data="resolvedChart" :options="chartOptions" />
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 6 — KPIs DOS CONSULTORES                                   -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div class="mb-4 flex items-center gap-2">
          <span
            class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-500/15"
          >
            <span
              class="i-lucide-users h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
            />
          </span>
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              KPIs dos Consultores
            </h2>
            <p class="text-[11px] text-n-slate-9">
              Tempo de resposta medido do transbordo até a 1ª mensagem do
              consultor
            </p>
          </div>
        </div>

        <div
          v-if="consultantKpis.length === 0"
          class="py-10 text-center text-sm text-n-slate-9"
        >
          Nenhum consultor com atendimentos resolvidos no período.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="pb-3 pr-4 text-left text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Agente
                </th>
                <th
                  class="pb-3 pr-4 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Conversas
                </th>
                <th
                  class="pb-3 pr-4 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Resposta após Transbordo
                </th>
                <th
                  class="pb-3 text-right text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Duração Real
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="agent in consultantKpis"
                :key="agent.id"
                class="border-b border-n-weak/50 transition-colors last:border-0 hover:bg-n-alpha-1"
              >
                <td class="py-3.5 pr-4">
                  <div class="flex items-center gap-2.5">
                    <div
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-600 dark:bg-violet-500/15 dark:text-violet-400"
                    >
                      {{ agent.name.charAt(0) }}
                    </div>
                    <span class="font-medium text-n-slate-12">{{
                      agent.name
                    }}</span>
                  </div>
                </td>
                <td
                  class="py-3.5 pr-4 text-right font-bold tabular-nums text-n-slate-12"
                >
                  {{ agent.conversations_count }}
                </td>
                <td class="py-3.5 pr-4 text-right tabular-nums text-n-slate-11">
                  {{ fmtSecs(agent.avg_first_response_seconds) }}
                </td>
                <td class="py-3.5 text-right tabular-nums text-n-slate-11">
                  {{ fmtSecs(agent.avg_conversation_seconds) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- BLOCO 7 — EVOLUÇÃO MENSAL                                         -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      <div
        v-if="monthlyEvolution.length"
        class="rounded-xl border border-n-weak bg-white p-5 dark:bg-n-alpha-2"
      >
        <div
          class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-2">
            <span
              class="flex h-7 w-7 items-center justify-center rounded-lg bg-woot-100 dark:bg-woot-500/15"
            >
              <span
                class="i-lucide-line-chart h-3.5 w-3.5 text-woot-600 dark:text-woot-400"
              />
            </span>
            <div>
              <!-- Trilha de navegação: Ano › Mês (clicável para voltar) -->
              <div class="flex items-center gap-1.5">
                <button
                  v-if="isDailyView"
                  class="text-xs font-medium text-n-slate-10 hover:text-woot-500"
                  @click="backToMonthly"
                >
                  {{ selectedYear }}
                </button>
                <span
                  v-if="isDailyView"
                  class="i-lucide-chevron-right h-3.5 w-3.5 text-n-slate-8"
                />
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{
                    isDailyView
                      ? MONTH_FULL[Number(selectedMonth) - 1]
                      : 'Evolução Mensal'
                  }}
                </h2>
              </div>
              <p class="text-[11px] text-n-slate-9">
                {{
                  isDailyView
                    ? `Dia a dia de ${selectedMonthLabel}`
                    : 'Por mês — clique num mês para abrir o dia a dia'
                }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 self-start sm:self-auto">
            <!-- Voltar para a visão mensal -->
            <button
              v-if="isDailyView"
              class="flex items-center gap-1.5 rounded-xl border border-n-weak bg-white px-3 py-2 text-sm font-medium text-n-slate-11 shadow-sm hover:bg-n-alpha-2 dark:bg-n-alpha-2"
              @click="backToMonthly"
            >
              <span class="i-lucide-arrow-left h-4 w-4" />
              Ver meses
            </button>
            <div
              class="flex items-center gap-2 rounded-xl border border-n-weak bg-white px-3 py-2 shadow-sm dark:bg-n-alpha-2"
            >
              <span class="i-lucide-calendar h-4 w-4 text-n-slate-10" />
              <select
                v-model="selectedYear"
                class="bg-transparent text-sm font-medium text-n-slate-12 outline-none"
              >
                <option v-for="y in availableYears" :key="y" :value="y">
                  {{ y }}
                </option>
              </select>
              <span class="h-4 w-px bg-n-weak" />
              <select
                v-model="selectedMonth"
                class="bg-transparent text-sm font-medium text-n-slate-12 outline-none"
              >
                <option :value="null">Ano todo</option>
                <option v-for="m in availableMonths" :key="m" :value="m">
                  {{ MONTH_FULL[Number(m) - 1] }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="relative h-72">
          <div
            v-if="dailyLoading"
            class="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/60 dark:bg-n-solid-2/60"
          >
            <span
              class="i-lucide-loader-2 h-5 w-5 animate-spin text-woot-500"
            />
          </div>
          <Bar :data="monthlyChart" :options="monthlyChartOptions" />
        </div>
        <!-- Totais do período em foco (ano ou mês selecionado) -->
        <div class="mt-4 flex flex-wrap gap-2 text-[11px]">
          <span
            class="rounded-lg bg-woot-500/10 px-2.5 py-1 font-semibold text-woot-600 dark:text-woot-400"
          >
            {{
              isDailyView
                ? `Total em ${selectedMonthLabel}`
                : `Total em ${selectedYear}`
            }}:
            {{ monthlyTotals.total }}
          </span>
          <span
            class="rounded-lg bg-green-500/10 px-2.5 py-1 font-semibold text-green-600 dark:text-green-400"
          >
            Concluídos: {{ monthlyTotals.resolved }}
          </span>
          <span
            class="rounded-lg bg-orange-500/10 px-2.5 py-1 font-semibold text-orange-600 dark:text-orange-400"
          >
            Transbordo: {{ pct(monthlyTotals.handoff, monthlyTotals.total) }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>
